import React from 'react';
import {
  AddIcon,
  Alert,
  Button,
  Card,
  CardContent,
  CheckIcon,
  Chip,
  Divider,
  ListPlusIcon,
  Spinner,
  Stack,
  Text,
  WarningIcon,
} from '@billda/design-system';
import { useSiteReimportStore } from '../../stores/siteReimportStore';

interface ReimportMissingLabourItemsCreatorProps {
  siteId: number;
  missingItems: string[];
}

const ReimportMissingLabourItemsCreator: React.FC<
  ReimportMissingLabourItemsCreatorProps
> = ({ siteId, missingItems }) => {
  const {
    createMissingLabourItem,
    createAllMissingLabourItems,
    creatingLabourItems,
    creationErrors,
    createdItems,
    selectedFile,
  } = useSiteReimportStore();

  const handleCreateItem = async (itemName: string) => {
    await createMissingLabourItem(itemName, {
      refreshData: true,
      siteId,
      selectedFile,
    });
  };

  const handleCreateAll = async () => {
    await createAllMissingLabourItems(siteId, selectedFile);
  };

  if (!missingItems.length) {
    return null;
  }

  const remainingItems = missingItems.filter(item => !createdItems.has(item));
  const hasRemainingItems = remainingItems.length > 0;
  const allItemsCreated =
    missingItems.length > 0 && remainingItems.length === 0;

  return (
    <Card
      className={
        allItemsCreated
          ? 'border border-border-success-subtle bg-surface-success-subtle'
          : 'border border-border-warning-subtle bg-surface-warning-subtle'
      }
      data-testid="reimport-missing-labour-items"
    >
      <CardContent className="p-6 pt-6">
        <Stack spacing={2}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <WarningIcon
                className={`size-4 ${
                  allItemsCreated
                    ? 'text-content-success'
                    : 'text-content-warning'
                }`}
                aria-hidden="true"
              />
              <Text
                variant="h6"
                className={`font-medium ${
                  allItemsCreated
                    ? 'text-content-success'
                    : 'text-content-warning'
                }`}
              >
                {allItemsCreated
                  ? 'All Labour Definitions Created'
                  : 'Missing Labour Definitions'}
              </Text>
              {!allItemsCreated && (
                <Chip
                  label={`${remainingItems.length} remaining`}
                  size="sm"
                  color="warning"
                  variant="outlined"
                />
              )}
            </div>

            {hasRemainingItems && (
              <Button
                variant="neutral"
                size="sm"
                loading={creatingLabourItems}
                onClick={handleCreateAll}
                disabled={creatingLabourItems}
                className="min-w-30"
                data-testid="reimport-create-all-button"
              >
                {creatingLabourItems ? null : (
                  <ListPlusIcon className="size-4" aria-hidden="true" />
                )}
                {creatingLabourItems ? 'Creating...' : 'Create All'}
              </Button>
            )}
          </div>

          <Text variant="body2" color="secondary">
            {allItemsCreated
              ? 'All missing labour definitions have been successfully created. You can now proceed with the re-import.'
              : 'The following labour item definitions are missing from your system. Create them individually or all at once to proceed.'}
          </Text>

          {creationErrors.length > 0 && (
            <>
              <Divider />
              <Alert severity="error">
                <Text variant="body2" className="mb-2 font-semibold">
                  Failed to create some items:
                </Text>
                <ul className="m-0 pl-4">
                  {creationErrors.map((error, index) => (
                    <li key={index}>
                      <Text variant="body2" as="span">
                        {error}
                      </Text>
                    </li>
                  ))}
                </ul>
              </Alert>
            </>
          )}

          <div>
            <Text
              variant="body2"
              color="secondary"
              className="mb-2 font-semibold"
            >
              Labour Items ({missingItems.length}):
            </Text>
            <div className="mt-2 flex flex-wrap gap-3">
              {missingItems.map(item => {
                const isCreated = createdItems.has(item);
                const isCurrentlyCreating = creatingLabourItems && !isCreated;

                return (
                  <Chip
                    key={item}
                    label={
                      isCreated ? (
                        <span className="flex items-center gap-1 font-medium">
                          {item}
                        </span>
                      ) : (
                        item
                      )
                    }
                    size="md"
                    variant={isCreated ? 'filled' : 'outlined'}
                    color={isCreated ? 'success' : 'warning'}
                    icon={
                      isCreated ? (
                        <CheckIcon className="size-3.5" aria-hidden="true" />
                      ) : undefined
                    }
                    deleteIcon={
                      !isCreated ? (
                        <span className="flex items-center">
                          {isCurrentlyCreating ? (
                            <Spinner size={14} />
                          ) : (
                            <AddIcon className="size-3.5" aria-hidden="true" />
                          )}
                        </span>
                      ) : undefined
                    }
                    onDelete={
                      !isCreated ? () => handleCreateItem(item) : undefined
                    }
                    className={
                      isCreated
                        ? '[&>button]:text-content-success'
                        : '[&>button]:text-content-accent'
                    }
                  />
                );
              })}
            </div>
          </div>

          {allItemsCreated && (
            <>
              <Divider />
              <Alert
                severity="success"
                icon={<CheckIcon className="size-5" aria-hidden="true" />}
              >
                <Text variant="body2" className="font-medium">
                  Perfect! All missing labour definitions have been created
                  successfully.
                </Text>
                <Text variant="caption" className="text-content-success">
                  The preview data will be automatically refreshed and you can
                  proceed to the next step.
                </Text>
              </Alert>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ReimportMissingLabourItemsCreator;
